const MASTERY_MIN_GAME_COUNT = 3;
const MASTERY_MIN_GRADE_SCORE = 0.72;
const QUEST_SESSION_WORD_COUNT = 40;
const TARGET_MASTERED = 5;
const TARGET_LAST_FAIL = 30;
const TARGET_NEVER_PLAYED = 5;

function parseDateForSort(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return 0;
  const m = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return 0;
  return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
}

function parseTimeForSort(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return 0;
  const parts = timeStr.trim().split(":");
  if (parts.length < 2) return 0;
  const hh = Number(parts[0]);
  const mm = Number(parts[1]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
  return hh * 60 + mm;
}

function getLastGameEntry(stats) {
  const last5 = stats?.last5Games;
  if (!Array.isArray(last5) || !last5.length) return null;
  return [...last5].sort((a, b) => {
    const dateDiff = parseDateForSort(b.date) - parseDateForSort(a.date);
    if (dateDiff !== 0) return dateDiff;
    return parseTimeForSort(b.time) - parseTimeForSort(a.time);
  })[0];
}

function getGameCount(word) {
  const count = word?.stats?.gameCount;
  return typeof count === "number" && count >= 0 ? count : 0;
}

function isNeverPlayedWord(word) {
  return getGameCount(word) === 0;
}

function isMasteredWord(word) {
  if (getGameCount(word) < MASTERY_MIN_GAME_COUNT) return false;
  const score = Number(word?.stats?.retention?.score);
  return Number.isFinite(score) && score >= MASTERY_MIN_GRADE_SCORE;
}

function isLastGameFailWord(word) {
  const last = getLastGameEntry(word?.stats);
  return last?.result === "fail";
}

function hasAnyFailInLast5(word) {
  const last5 = word?.stats?.last5Games;
  if (!Array.isArray(last5)) return false;
  return last5.some((entry) => entry?.result === "fail");
}

function shuffle(items, rng = Math.random) {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function takeUnique(pool, count, selectedIds) {
  const picked = [];
  for (const word of pool) {
    if (picked.length >= count) break;
    const id = word?.mot;
    if (!id || selectedIds.has(id)) continue;
    selectedIds.add(id);
    picked.push(word);
  }
  return picked;
}

/**
 * Sélectionne 40 mots uniques pour une session de quête :
 * 5 maîtrisés, 30 dernier échec, 5 jamais joués,
 * avec cascade de repli si un vivier est insuffisant.
 */
export function buildQuestTrainingWordPool(words = [], rng = Math.random) {
  const catalog = Array.isArray(words) ? words.filter((word) => word?.mot) : [];
  if (!catalog.length) return [];

  const mastered = shuffle(catalog.filter(isMasteredWord), rng);
  const lastFail = shuffle(catalog.filter(isLastGameFailWord), rng);
  const neverPlayed = shuffle(catalog.filter(isNeverPlayedWord), rng);
  const anyFail = shuffle(catalog.filter(hasAnyFailInLast5), rng);
  const weak = shuffle(
    catalog.filter((word) => !isMasteredWord(word) && !isNeverPlayedWord(word)),
    rng
  );
  const allShuffled = shuffle(catalog, rng);

  const selectedIds = new Set();
  const selected = [];

  selected.push(...takeUnique(neverPlayed, TARGET_NEVER_PLAYED, selectedIds));
  selected.push(...takeUnique(lastFail, TARGET_LAST_FAIL, selectedIds));
  selected.push(...takeUnique(mastered, TARGET_MASTERED, selectedIds));

  const fillers = [lastFail, anyFail, mastered, neverPlayed, weak, allShuffled];
  for (const pool of fillers) {
    if (selected.length >= QUEST_SESSION_WORD_COUNT) break;
    selected.push(
      ...takeUnique(pool, QUEST_SESSION_WORD_COUNT - selected.length, selectedIds)
    );
  }

  return shuffle(selected.slice(0, QUEST_SESSION_WORD_COUNT), rng);
}

export const QUEST_TRAINING_SESSION_SECONDS = 20 * 60;
export const QUEST_TRAINING_WORD_SECONDS = 30;
export const QUEST_TRAINING_WORD_DWELL_SECONDS = 10;
export const QUEST_TRAINING_PRESENCE_MIN_GAP_SECONDS = 5 * 60;
export const QUEST_TRAINING_PRESENCE_WINDOW_SECONDS = 10;
export const QUEST_TRAINING_PRESENCE_COUNT = 2;

/** Tire 2 instants de test de présence, espacés d'au moins 5 minutes. */
export function schedulePresenceChecks(
  sessionSeconds = QUEST_TRAINING_SESSION_SECONDS,
  rng = Math.random
) {
  const minGap = QUEST_TRAINING_PRESENCE_MIN_GAP_SECONDS;
  const earliest = 45;
  const latest = Math.max(earliest + minGap + 45, sessionSeconds - 45);
  const span = latest - earliest;

  let first = earliest + Math.floor(rng() * Math.max(1, span - minGap));
  let second = first + minGap + Math.floor(rng() * Math.max(1, latest - (first + minGap) + 1));

  if (second > latest) {
    second = latest;
    first = Math.max(earliest, second - minGap - Math.floor(rng() * 60));
  }

  return [first, second].sort((a, b) => a - b);
}
